import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(`Bem-vindo de volta, ${user.name}`);
      if (user.role === "client") navigate(`/cliente/${user.id}`);
      else navigate("/admin");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erro ao iniciar a minha sessão");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-[#F9F6F0]">
      {/* Left brand side */}
      <div className="md:w-1/2 relative bg-[#3B2B23] text-[#F9F6F0] p-6 sm:p-10 md:p-12 lg:p-16 flex flex-col justify-between overflow-hidden min-h-[180px] md:min-h-screen">
        <div className="grain absolute inset-0 opacity-30 pointer-events-none" />

        <div className="relative z-10 flex items-center gap-3 sm:gap-4">
          <img
            src="/unlimited-logo.png"
            alt="Unlimited Studio"
            className="w-10 h-10 sm:w-14 sm:h-14 object-contain bg-[#F9F6F0] rounded-full p-1.5 sm:p-2"
            data-testid="brand-logo"
          />
          <div>
            <div className="text-xs sm:text-sm uppercase tracking-[0.3em] text-[#C07C5B]">Unlimited Studio</div>
            <div className="text-[9px] sm:text-[10px] tracking-wider text-[#E6DED6]/70 mt-0.5 sm:mt-1">Strategic Branding &amp; Communication</div>
          </div>
        </div>

        <div className="relative z-10 fade-up hidden md:block">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl leading-tight">
            Bem-vindo ao<br />
            <span className="italic text-[#C07C5B]">Unlimited Studio.</span>
          </h1>
        </div>

        <div className="relative z-10 text-[10px] tracking-wider text-[#E6DED6]/60 uppercase hidden md:block">
          © {new Date().getFullYear()} — Unlimited Studio · Gestão de Redes Sociais
        </div>
      </div>

      {/* Right form side */}
      <div className="md:w-1/2 flex items-center justify-center p-6 sm:p-10 md:p-12 lg:p-16 flex-1">
        <form
          onSubmit={submit}
          className="w-full max-w-md fade-up"
          data-testid="login-form"
        >
          <h2 className="text-3xl sm:text-4xl text-[#1A1412]">A minha sessão</h2>

          <div className="mt-10 space-y-5">
            <div>
              <Label htmlFor="email" className="text-[#3B2B23] text-[10px] uppercase tracking-[0.3em]">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="nome@empresa.pt"
                className="mt-2 h-12 bg-white border-[#EAE3DB] focus-visible:ring-[#8B5E3C]"
                required
                data-testid="login-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-[#3B2B23] text-[10px] uppercase tracking-[0.3em]">
                Palavra-passe
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-2 h-12 bg-white border-[#EAE3DB] focus-visible:ring-[#8B5E3C]"
                required
                data-testid="login-password-input"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#3B2B23] hover:bg-[#2A1E18] text-[#F9F6F0] rounded-full text-xs tracking-[0.3em] uppercase transition-all"
              data-testid="login-submit-button"
            >
              {loading ? "A entrar…" : "Entrar"}
            </Button>
          </div>

          <div className="mt-6 text-center">
            <Link
              to="/forgot-password"
              className="text-[11px] text-[#8B5E3C] hover:text-[#3B2B23] uppercase tracking-[0.2em] transition-colors"
              data-testid="forgot-password-link"
            >
              Esqueci-me da palavra-passe
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
